import { Effect, Context } from "effect";
import {
  CabinConfiguration,
  SeatOccupancy,
  Seat,
  CabinLayout,
} from "../models/index.js";

/**
 * A320 Neo cabin constants.
 * - Total rows: 30 (numbered 1-31, skipping 13)
 * - Business class: 4 seats per row (A-C, D-F with B and E blocked)
 * - Economy class: 6 seats per row (A-F)
 */
const TOTAL_ROWS = 30;
const BUSINESS_SEATS_PER_ROW = 4; // A, C, D, F (B and E are blocked)
const ECONOMY_SEATS_PER_ROW = 6; // A, B, C, D, E, F
const SEAT_LETTERS = ["A", "B", "C", "D", "E", "F"] as const;
const BLOCKED_BUSINESS_SEATS = ["B", "E"] as const;

/**
 * Convert internal row index (0-29) to display row number (1-31, skipping 13).
 */
const toDisplayRowNumber = (index: number): number => {
  const rowNum = index + 1;
  return rowNum >= 13 ? rowNum + 1 : rowNum;
};

/**
 * Service interface for seat calculations.
 */
export class SeatCalculatorService extends Context.Tag("SeatCalculatorService")<
  SeatCalculatorService,
  {
    /**
     * Calculate seat occupancy metrics from cabin configuration.
     */
    readonly calculateOccupancy: (
      config: CabinConfiguration
    ) => Effect.Effect<SeatOccupancy, never, never>;

    /**
     * Generate the complete cabin layout with seat states.
     */
    readonly generateLayout: (
      config: CabinConfiguration
    ) => Effect.Effect<CabinLayout, never, never>;
  }
>() {}

/**
 * Live implementation of the SeatCalculatorService.
 */
export const SeatCalculatorServiceLive = {
  calculateOccupancy: (config: CabinConfiguration) =>
    Effect.gen(function*() {
      yield* Effect.annotateCurrentSpan({
        "cabin.business_passengers": config.businessPassengers,
        "cabin.economy_passengers": config.economyPassengers,
        "cabin.lap_infants": config.lapInfants,
      });

      // Calculate business rows needed (ceiling division)
      const businessRows =
        config.businessPassengers > 0
          ? Math.ceil(config.businessPassengers / BUSINESS_SEATS_PER_ROW)
          : 0;

      // Remaining rows are economy
      const economyRows = TOTAL_ROWS - businessRows;

      // Calculate total available seats
      const businessSeats = businessRows * BUSINESS_SEATS_PER_ROW;
      const economySeats = economyRows * ECONOMY_SEATS_PER_ROW;
      const totalAvailableSeats = businessSeats + economySeats;

      // Economy passengers needing seats (subtract lap infants)
      const economyPassengersNeedingSeats = Math.max(
        0,
        config.economyPassengers - config.lapInfants
      );

      // Total occupied seats
      const occupiedSeats =
        config.businessPassengers + economyPassengersNeedingSeats;

      // Available seats (can be negative if overbooked)
      const availableSeats = totalAvailableSeats - occupiedSeats;

      // Utilization percentage
      const utilizationPercent =
        totalAvailableSeats > 0
          ? (occupiedSeats / totalAvailableSeats) * 100
          : 0;

      // Overbooking count
      const overbookedCount = Math.max(0, -availableSeats);

      // Lap infant warning
      const lapInfantWarning = config.lapInfants > config.economyPassengers;

      yield* Effect.annotateCurrentSpan({
        "result.business_rows": businessRows,
        "result.economy_rows": economyRows,
        "result.total_capacity": totalAvailableSeats,
        "result.occupied_seats": occupiedSeats,
        "result.utilization_percent": utilizationPercent,
        "result.is_overbooked": overbookedCount > 0,
      });

      return new SeatOccupancy({
        businessRows,
        economyRows,
        totalAvailableSeats,
        occupiedSeats,
        availableSeats,
        utilizationPercent,
        overbookedCount,
        lapInfantWarning,
      });
    }).pipe(Effect.withSpan("SeatCalculator.calculateOccupancy")),

  generateLayout: (config: CabinConfiguration) =>
    Effect.gen(function*() {
      const occupancy = yield* SeatCalculatorServiceLive.calculateOccupancy(
        config
      );

      const seats: Seat[] = [];
      let businessSeatsOccupied = 0;
      let economySeatsOccupied = 0;
      let lapInfantsAssigned = 0;

      const economyPassengersNeedingSeats = Math.max(
        0,
        config.economyPassengers - config.lapInfants
      );

      // Generate all rows
      for (let rowIndex = 0; rowIndex < TOTAL_ROWS; rowIndex++) {
        const displayRow = toDisplayRowNumber(rowIndex);
        const isBusinessRow = rowIndex < occupancy.businessRows;

        for (const letter of SEAT_LETTERS) {
          const isBlockedSeat =
            isBusinessRow &&
            BLOCKED_BUSINESS_SEATS.includes(letter as "B" | "E");

          let isOccupied = false;
          let hasLapInfant = false;

          if (!isBlockedSeat) {
            if (isBusinessRow) {
              // Fill business seats
              if (businessSeatsOccupied < config.businessPassengers) {
                isOccupied = true;
                businessSeatsOccupied++;
              }
            } else {
              // Fill economy seats
              if (economySeatsOccupied < economyPassengersNeedingSeats) {
                isOccupied = true;
                economySeatsOccupied++;

                // Assign lap infants to first N occupied economy seats
                if (lapInfantsAssigned < config.lapInfants) {
                  hasLapInfant = true;
                  lapInfantsAssigned++;
                }
              }
            }
          }

          seats.push(
            new Seat({
              row: displayRow,
              letter: letter,
              isBlocked: isBlockedSeat,
              isOccupied,
              hasLapInfant,
              cabinClass: isBusinessRow ? "business" : "economy",
            })
          );
        }
      }

      return new CabinLayout({
        seats,
        occupancy,
      });
    }).pipe(Effect.withSpan("SeatCalculator.generateLayout")),
};

/**
 * Layer providing the SeatCalculatorService.
 */
export const SeatCalculatorServiceLayer = Effect.provideService(
  Effect.void,
  SeatCalculatorService,
  SeatCalculatorServiceLive
);
